import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { getCurrentUser } from '../../lib/auth';
import toast from 'react-hot-toast';
import { FaReply, FaTrash, FaEdit, FaTimes } from 'react-icons/fa';

interface Comment {
  id: string;
  content: string;
  user_id: string;
  parent_id: string | null;
  created_at: string;
  likes_count: number;
  user: {
    profile_image: string | null;
  };
  replies?: Comment[];
}

interface CommentSectionProps {
  videoId: string;
  isVisible: boolean;
  onCommentAdded?: () => void;
  onClose?: () => void;
}

const CommentSection: React.FC<CommentSectionProps> = ({ 
  videoId, 
  isVisible, 
  onCommentAdded,
  onClose 
}) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const user = getCurrentUser();
  const commentListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isVisible) {
      loadComments();
    }
  }, [isVisible, videoId]);

  const loadComments = async () => {
    try {
      setLoading(true);
      const { data: commentsData, error } = await supabase
        .from('ponovideocomments')
        .select(`
          id,
          content,
          user_id,
          parent_id,
          created_at,
          likes_count,
          user:ponousers!user_id (
            profile_image
          )
        `)
        .eq('video_id', videoId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Organize comments into threads
      const threadedComments = commentsData?.reduce((acc: Comment[], comment: Comment) => {
        if (!comment.parent_id) {
          // This is a root comment
          comment.replies = commentsData.filter(c => c.parent_id === comment.id);
          acc.push(comment);
        }
        return acc;
      }, []) || [];

      setComments(threadedComments);
    } catch (error: any) {
      console.error('Error loading comments:', error);
      toast.error('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please log in to comment');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('ponovideocomments')
        .insert({
          video_id: videoId,
          user_id: user.id,
          content: newComment,
          parent_id: replyTo
        })
        .select(`
          id,
          content,
          user_id,
          parent_id,
          created_at,
          likes_count,
          user:ponousers!user_id (
            profile_image
          )
        `)
        .single();

      if (error) throw error;

      toast.success('Comment posted successfully');
      setNewComment('');
      setReplyTo(null);
      loadComments();
      onCommentAdded?.();
    } catch (error: any) {
      console.error('Error posting comment:', error);
      toast.error('Failed to post comment');
    }
  };

  const handleEdit = async (commentId: string) => {
    try {
      const { error } = await supabase
        .from('ponovideocomments')
        .update({ content: editContent })
        .eq('id', commentId)
        .eq('user_id', user?.id);

      if (error) throw error;

      toast.success('Comment updated');
      setEditingComment(null);
      loadComments();
    } catch (error: any) {
      console.error('Error updating comment:', error);
      toast.error('Failed to update comment');
    }
  };

  const handleDelete = async (commentId: string) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      const { error } = await supabase
        .from('ponovideocomments')
        .delete()
        .eq('id', commentId)
        .eq('user_id', user?.id);

      if (error) throw error;

      toast.success('Comment deleted');
      loadComments();
      onCommentAdded?.();
    } catch (error: any) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderComment = (comment: Comment, isReply = false) => (
    <div 
      key={comment.id} 
      className={`p-4 ${isReply ? 'ml-8 border-l border-gray-800' : 'border-b border-gray-800'}`}
    >
      <div className="flex items-start space-x-3">
        <img
          src={comment.user?.profile_image || `https://api.dicebear.com/7.x/initials/svg?seed=${comment.user_id}`}
          alt="Profile"
          className="w-10 h-10 rounded-full object-cover"
        />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">
              {formatDate(comment.created_at)}
            </span>
          </div>

          {editingComment === comment.id ? (
            <div className="mt-2">
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 rounded-lg text-white resize-none"
                rows={3}
              />
              <div className="flex justify-end space-x-2 mt-2">
                <button
                  onClick={() => setEditingComment(null)}
                  className="px-3 py-1 text-sm text-gray-400 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleEdit(comment.id)}
                  className="px-3 py-1 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <p className="mt-1 text-gray-300">{comment.content}</p>
          )}

          <div className="flex items-center space-x-4 mt-2">
            <button
              onClick={() => setReplyTo(comment.id)}
              className="text-sm text-gray-400 hover:text-white flex items-center"
            >
              <FaReply className="w-4 h-4 mr-1" />
              Reply
            </button>
            {user?.id === comment.user_id && (
              <>
                <button
                  onClick={() => {
                    setEditingComment(comment.id);
                    setEditContent(comment.content);
                  }}
                  className="text-sm text-gray-400 hover:text-white flex items-center"
                >
                  <FaEdit className="w-4 h-4 mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(comment.id)}
                  className="text-sm text-red-500 hover:text-red-400 flex items-center"
                >
                  <FaTrash className="w-4 h-4 mr-1" />
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {comment.replies?.map(reply => renderComment(reply, true))}

      {replyTo === comment.id && (
        <div className="mt-4 ml-8">
          <form onSubmit={handleSubmit} className="space-y-2">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Write a reply..."
              className="w-full px-3 py-2 bg-gray-800 rounded-lg text-white resize-none"
              rows={3}
            />
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => setReplyTo(null)}
                className="px-3 py-1 text-sm text-gray-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-3 py-1 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Reply
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );

  if (!isVisible) return null;

  return (
    <div className="fixed inset-y-0 right-0 w-96 bg-black bg-opacity-90 z-50 flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center p-4 border-b border-gray-800">
        <h2 className="text-xl font-bold text-white">Comments</h2>
        <button
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-white"
        >
          <FaTimes className="w-6 h-6" />
        </button>
      </div>

      {/* Comment Form */}
      <form onSubmit={handleSubmit} className="p-4 border-b border-gray-800">
        <div className="flex items-start space-x-4">
          <img
            src={user?.profile_image || `https://api.dicebear.com/7.x/initials/svg?seed=${user?.id}`}
            alt="Your profile"
            className="w-10 h-10 rounded-full object-cover"
          />
          <div className="flex-1">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Write a comment..."
              className="w-full px-3 py-2 bg-gray-800 rounded-lg text-white resize-none"
              rows={3}
            />
            <div className="flex justify-end mt-2">
              <button
                type="submit"
                disabled={!newComment.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Comment
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Comments List */}
      <div ref={commentListRef} className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center text-gray-500">Loading comments...</div>
        ) : comments.length > 0 ? (
          comments.map(comment => renderComment(comment))
        ) : (
          <div className="p-4 text-center text-gray-500">No comments yet</div>
        )}
      </div>
    </div>
  );
};

export default CommentSection;